/**
 * Seed data for demo mode.
 *
 * These are shaped like the Mongo documents in `backend/src/models`, not like
 * API responses — the handlers run them through the same serializers the
 * backend uses, so what the demo returns matches production byte for byte.
 *
 * All of it is synthetic. No real member data is ever included here, because
 * this file ships in the public JS bundle.
 */

import {
  EVENT_IDS,
  HARVEST_LOG_IDS,
  HARVEST_PRODUCT_IDS,
  LOCATION_IDS,
  ORDER_IDS,
  PICKUP_IDS,
  SEASON_ID,
  SUBSCRIPTION_IDS,
  TIER_IDS,
  USER_IDS,
} from "./ids";

export interface DemoUser {
  _id: string;
  firstName: string;
  secondName?: string;
  surname: string;
  email: string;
  role: string;
  phoneNumber?: string;
  emailVerified: boolean;
  createdAt: string;
  companyName?: string;
  companyRegistrationNumber?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  shippingHeavyPrice?: number;
  shippingLightPrice?: number;
  subscriptionHistory: {
    subscriptionTierId: string;
    subscriptionTierName: string;
    year: number;
  }[];
}

export interface DemoPickup {
  _id: string;
  userId: string;
  pickupLocationId: string;
  signupPickupLocationId?: string;
  pickupDay: string;
  subscriptionTierId: string;
  subscriptionPaymentStatus: "pending" | "paid" | "failed" | "expired";
  paidAt?: string;
  friendPickupFirstName?: string | null;
  friendPickupLastName?: string | null;
  friendPickupPhone?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DemoPickupLocation {
  _id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  days: { day: string; startTime: string; endTime: string }[];
  undeletable?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DemoSubscription {
  _id: string;
  name: string;
  spots?: number;
  isCombo?: boolean;
  isActive: boolean;
}

export interface DemoTier {
  _id: string;
  subscriptionId: string;
  name: string;
  price: number;
  oldCsaMemberPrice: number;
  spots?: number;
  dependsOn?: string;
  takesFrom?: string[];
}

export interface DemoHarvestProduct {
  _id: string;
  name: string;
  quantity: number;
  price: number;
  discount: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface DemoHarvestLog {
  _id: string;
  startDate: string;
  endDate: string;
  comment: string;
  products: { product: string; quantity: number }[];
  createdAt: string;
}

export interface DemoOrder {
  _id: string;
  companyRegistrationNumber: string;
  totalPrice: number;
  deliveryMethod: "pickup" | "shipping";
  companyAddress?: string;
  companyPostalCode?: string;
  companyCity?: string;
  shippingPrice?: number;
  status: string;
  products: {
    productName: string;
    unitPrice: number;
    quantity: number;
    totalPrice: number;
  }[];
  createdAt: string;
  updatedAt: string;
}

export interface DemoSeason {
  _id: string;
  areWeInSeason: boolean;
  isBusinessOpen: boolean;
  applicationPeriod: string;
  freeDeliveryThreshold: number;
  businessDeliveryPrice: number;
  csaDeliveryPrice: number;
  checkboxes: {
    printHarvestLogs: boolean;
    printOrders: boolean;
    updatePickupLocations: boolean;
    updateSubscriptionsAndTiers: boolean;
    updateCsaDeliveryPrice: boolean;
  };
  waitlist: string[];
  createdAt: string;
  updatedAt: string;
}

/** Shaped as the admin event-ticket API returns it, not as the Event model. */
export interface DemoEvent {
  _id: string;
  eventName: string;
  eventStartTime: string;
  eventEndTime: string;
  locationOfEvent: string;
  numberOfAvailableTickets?: number;
  purchases: {
    buyerName: string;
    buyerEmail: string;
    quantity: number;
    confirmationCode: string;
    comment?: string;
    purchasedAt: string;
  }[];
}

export interface DemoData {
  users: DemoUser[];
  pickups: DemoPickup[];
  pickupLocations: DemoPickupLocation[];
  subscriptions: DemoSubscription[];
  tiers: DemoTier[];
  harvestProducts: DemoHarvestProduct[];
  harvestLogs: DemoHarvestLog[];
  orders: DemoOrder[];
  events: DemoEvent[];
  season: DemoSeason;
}

const SEASON_YEAR = 2026;

export const DEMO_USERS: DemoUser[] = [
  {
    _id: USER_IDS.admin,
    firstName: "Demo",
    surname: "Administrator",
    email: "demo.admin@alnarpsfarm.example",
    role: "admin",
    phoneNumber: "+46 70 000 00 01",
    emailVerified: true,
    createdAt: "2024-02-14T09:12:00.000Z",
    subscriptionHistory: [],
  },
  {
    _id: USER_IDS.employee,
    firstName: "Demo",
    surname: "Employee",
    email: "demo.employee@alnarpsfarm.example",
    role: "employee",
    phoneNumber: "+46 70 000 00 02",
    emailVerified: true,
    createdAt: "2024-03-02T08:00:00.000Z",
    subscriptionHistory: [],
  },
  {
    _id: USER_IDS.business,
    firstName: "Demo",
    surname: "Business",
    email: "demo.business@alnarpsfarm.example",
    role: "business",
    phoneNumber: "+46 70 000 00 03",
    emailVerified: true,
    createdAt: "2024-04-18T13:45:00.000Z",
    companyName: "Restaurang Gröna Bordet",
    companyRegistrationNumber: "556677-8899",
    address: "Storgatan 14",
    postalCode: "211 42",
    city: "Malmö",
    shippingHeavyPrice: 180,
    shippingLightPrice: 90,
    subscriptionHistory: [],
  },
  {
    _id: USER_IDS.client,
    firstName: "Demo",
    surname: "Member",
    email: "demo.member@alnarpsfarm.example",
    role: "social",
    phoneNumber: "+46 70 000 00 04",
    emailVerified: true,
    createdAt: "2025-01-20T17:30:00.000Z",
    subscriptionHistory: [
      {
        subscriptionTierId: TIER_IDS.lowIncome,
        subscriptionTierName: "Low-income",
        year: SEASON_YEAR - 1,
      },
      {
        subscriptionTierId: TIER_IDS.regular,
        subscriptionTierName: "Regular",
        year: SEASON_YEAR,
      },
    ],
  },
  {
    _id: USER_IDS.member1,
    firstName: "Astrid",
    surname: "Lindqvist",
    email: "astrid.lindqvist@example.com",
    role: "social",
    phoneNumber: "+46 70 111 11 11",
    emailVerified: true,
    createdAt: "2025-02-11T10:05:00.000Z",
    subscriptionHistory: [
      {
        subscriptionTierId: TIER_IDS.regular,
        subscriptionTierName: "Regular",
        year: SEASON_YEAR,
      },
    ],
  },
  {
    _id: USER_IDS.member2,
    firstName: "Johan",
    secondName: "Erik",
    surname: "Nyberg",
    email: "johan.nyberg@example.com",
    role: "social",
    phoneNumber: "+46 70 222 22 22",
    emailVerified: true,
    createdAt: "2025-02-19T11:20:00.000Z",
    subscriptionHistory: [
      {
        subscriptionTierId: TIER_IDS.solidarity,
        subscriptionTierName: "Solidarity",
        year: SEASON_YEAR,
      },
    ],
  },
  {
    _id: USER_IDS.member3,
    firstName: "Maria",
    surname: "Ferreira",
    email: "maria.ferreira@example.com",
    role: "social",
    phoneNumber: "+46 70 333 33 33",
    emailVerified: true,
    createdAt: "2025-03-04T09:00:00.000Z",
    subscriptionHistory: [
      {
        subscriptionTierId: TIER_IDS.vegetablesAndFlowers,
        subscriptionTierName: "Vegetables & Flowers",
        year: SEASON_YEAR,
      },
    ],
  },
  {
    _id: USER_IDS.member4,
    firstName: "Oskar",
    surname: "Bergström",
    email: "oskar.bergstrom@example.com",
    role: "social",
    phoneNumber: "+46 70 444 44 44",
    emailVerified: true,
    createdAt: "2025-03-15T14:40:00.000Z",
    subscriptionHistory: [
      {
        subscriptionTierId: TIER_IDS.flowers,
        subscriptionTierName: "Flowers",
        year: SEASON_YEAR,
      },
    ],
  },
  {
    _id: USER_IDS.member5,
    firstName: "Sanna",
    surname: "Holm",
    email: "sanna.holm@example.com",
    role: "social",
    phoneNumber: "+46 70 555 55 55",
    emailVerified: true,
    createdAt: "2025-04-01T08:25:00.000Z",
    subscriptionHistory: [
      {
        subscriptionTierId: TIER_IDS.lowIncome,
        subscriptionTierName: "Low-income",
        year: SEASON_YEAR,
      },
    ],
  },
  {
    _id: USER_IDS.member6,
    firstName: "Tobias",
    surname: "Krantz",
    email: "tobias.krantz@example.com",
    role: "social",
    phoneNumber: "+46 70 666 66 66",
    emailVerified: true,
    createdAt: "2025-04-22T16:10:00.000Z",
    subscriptionHistory: [
      {
        subscriptionTierId: TIER_IDS.regular,
        subscriptionTierName: "Regular",
        year: SEASON_YEAR,
      },
    ],
  },
  {
    _id: USER_IDS.member7,
    firstName: "Elin",
    surname: "Sandberg",
    email: "elin.sandberg@example.com",
    role: "social",
    phoneNumber: "+46 70 777 77 77",
    emailVerified: true,
    createdAt: "2025-05-09T12:00:00.000Z",
    subscriptionHistory: [
      {
        subscriptionTierId: TIER_IDS.solidarity,
        subscriptionTierName: "Solidarity",
        year: SEASON_YEAR,
      },
    ],
  },
  {
    // Deliberately has no pickup — exercises the "assign subscription" admin flow.
    _id: USER_IDS.unassigned,
    firstName: "Peter",
    surname: "Wallin",
    email: "peter.wallin@example.com",
    role: "social",
    phoneNumber: "+46 70 888 88 88",
    emailVerified: false,
    createdAt: "2026-06-30T19:15:00.000Z",
    subscriptionHistory: [],
  },
];

export const DEMO_PICKUP_LOCATIONS: DemoPickupLocation[] = [
  {
    _id: LOCATION_IDS.farm,
    name: "Alnarp Farm",
    address: "Sundsvägen 6, 234 56 Alnarp",
    latitude: 55.6581,
    longitude: 13.0742,
    days: [
      { day: "Wednesday", startTime: "15:00", endTime: "18:00" },
      { day: "Friday", startTime: "14:00", endTime: "17:00" },
    ],
    undeletable: true,
    createdAt: "2024-01-10T10:00:00.000Z",
    updatedAt: "2026-03-01T10:00:00.000Z",
  },
  {
    _id: LOCATION_IDS.malmo,
    name: "Malmö – Möllevången",
    address: "Möllevångstorget 1, 214 24 Malmö",
    latitude: 55.5911,
    longitude: 13.0132,
    days: [{ day: "Thursday", startTime: "16:00", endTime: "19:00" }],
    createdAt: "2024-01-10T10:05:00.000Z",
    updatedAt: "2026-03-01T10:05:00.000Z",
  },
  {
    _id: LOCATION_IDS.lund,
    name: "Lund – Clemenstorget",
    address: "Clemenstorget 8, 222 21 Lund",
    latitude: 55.7075,
    longitude: 13.1899,
    days: [{ day: "Tuesday", startTime: "15:30", endTime: "18:30" }],
    createdAt: "2024-01-10T10:10:00.000Z",
    updatedAt: "2026-03-01T10:10:00.000Z",
  },
];

export const DEMO_SUBSCRIPTIONS: DemoSubscription[] = [
  {
    _id: SUBSCRIPTION_IDS.vegetables,
    name: "Vegetables",
    spots: 60,
    isActive: true,
  },
  {
    _id: SUBSCRIPTION_IDS.flowers,
    name: "Flowers",
    spots: 20,
    isActive: true,
  },
  {
    _id: SUBSCRIPTION_IDS.vegetablesAndFlowers,
    name: "Vegetables & Flowers",
    isCombo: true,
    isActive: true,
  },
];

/**
 * Prices mirror the real "Tiers and prices for this season" page. No tier sets
 * its own `spots`: the three vegetable tiers share the 60-spot Vegetables pool,
 * which is what makes the solidarity model work.
 */
export const DEMO_TIERS: DemoTier[] = [
  {
    _id: TIER_IDS.regular,
    subscriptionId: SUBSCRIPTION_IDS.vegetables,
    name: "Regular",
    price: 6930,
    oldCsaMemberPrice: 6600,
    takesFrom: [],
  },
  {
    _id: TIER_IDS.solidarity,
    subscriptionId: SUBSCRIPTION_IDS.vegetables,
    name: "Solidarity",
    price: 8316,
    oldCsaMemberPrice: 7900,
    takesFrom: [],
  },
  {
    _id: TIER_IDS.lowIncome,
    subscriptionId: SUBSCRIPTION_IDS.vegetables,
    name: "Low-income",
    price: 5544,
    oldCsaMemberPrice: 5300,
    takesFrom: [],
  },
  {
    _id: TIER_IDS.flowers,
    subscriptionId: SUBSCRIPTION_IDS.flowers,
    name: "Flowers",
    price: 2700,
    oldCsaMemberPrice: 2560,
    takesFrom: [],
  },
  {
    _id: TIER_IDS.vegetablesAndFlowers,
    subscriptionId: SUBSCRIPTION_IDS.vegetablesAndFlowers,
    name: "Vegetables & Flowers",
    price: 9630,
    oldCsaMemberPrice: 9160,
    // Consumes a spot from both underlying pools.
    takesFrom: [SUBSCRIPTION_IDS.vegetables, SUBSCRIPTION_IDS.flowers],
  },
];

export const DEMO_PICKUPS: DemoPickup[] = [
  {
    _id: PICKUP_IDS.client,
    userId: USER_IDS.client,
    pickupLocationId: LOCATION_IDS.malmo,
    signupPickupLocationId: LOCATION_IDS.malmo,
    pickupDay: "Thursday",
    subscriptionTierId: TIER_IDS.regular,
    subscriptionPaymentStatus: "paid",
    paidAt: "2026-03-12T09:31:00.000Z",
    friendPickupFirstName: null,
    friendPickupLastName: null,
    friendPickupPhone: null,
    createdAt: "2026-03-10T18:02:00.000Z",
    updatedAt: "2026-03-12T09:31:00.000Z",
  },
  {
    _id: PICKUP_IDS.member1,
    userId: USER_IDS.member1,
    pickupLocationId: LOCATION_IDS.farm,
    signupPickupLocationId: LOCATION_IDS.farm,
    pickupDay: "Wednesday",
    subscriptionTierId: TIER_IDS.regular,
    subscriptionPaymentStatus: "paid",
    paidAt: "2026-03-04T11:12:00.000Z",
    friendPickupFirstName: "Nils",
    friendPickupLastName: "Lindqvist",
    friendPickupPhone: "+46 70 999 99 99",
    createdAt: "2026-03-02T10:00:00.000Z",
    updatedAt: "2026-03-04T11:12:00.000Z",
  },
  {
    _id: PICKUP_IDS.member2,
    userId: USER_IDS.member2,
    pickupLocationId: LOCATION_IDS.lund,
    signupPickupLocationId: LOCATION_IDS.lund,
    pickupDay: "Tuesday",
    subscriptionTierId: TIER_IDS.solidarity,
    subscriptionPaymentStatus: "paid",
    paidAt: "2026-03-06T14:22:00.000Z",
    friendPickupFirstName: null,
    friendPickupLastName: null,
    friendPickupPhone: null,
    createdAt: "2026-03-05T09:45:00.000Z",
    updatedAt: "2026-03-06T14:22:00.000Z",
  },
  {
    _id: PICKUP_IDS.member3,
    userId: USER_IDS.member3,
    pickupLocationId: LOCATION_IDS.farm,
    signupPickupLocationId: LOCATION_IDS.farm,
    pickupDay: "Friday",
    subscriptionTierId: TIER_IDS.vegetablesAndFlowers,
    subscriptionPaymentStatus: "paid",
    paidAt: "2026-03-09T08:05:00.000Z",
    friendPickupFirstName: null,
    friendPickupLastName: null,
    friendPickupPhone: null,
    createdAt: "2026-03-08T20:30:00.000Z",
    updatedAt: "2026-03-09T08:05:00.000Z",
  },
  {
    _id: PICKUP_IDS.member4,
    userId: USER_IDS.member4,
    pickupLocationId: LOCATION_IDS.malmo,
    signupPickupLocationId: LOCATION_IDS.malmo,
    pickupDay: "Thursday",
    subscriptionTierId: TIER_IDS.flowers,
    // Unpaid — exercises the payment-reminder admin action.
    subscriptionPaymentStatus: "pending",
    friendPickupFirstName: null,
    friendPickupLastName: null,
    friendPickupPhone: null,
    createdAt: "2026-04-02T12:15:00.000Z",
    updatedAt: "2026-04-02T12:15:00.000Z",
  },
  {
    _id: PICKUP_IDS.member5,
    userId: USER_IDS.member5,
    pickupLocationId: LOCATION_IDS.lund,
    signupPickupLocationId: LOCATION_IDS.lund,
    pickupDay: "Tuesday",
    subscriptionTierId: TIER_IDS.lowIncome,
    subscriptionPaymentStatus: "paid",
    paidAt: "2026-04-06T10:40:00.000Z",
    friendPickupFirstName: null,
    friendPickupLastName: null,
    friendPickupPhone: null,
    createdAt: "2026-04-05T17:55:00.000Z",
    updatedAt: "2026-04-06T10:40:00.000Z",
  },
  {
    _id: PICKUP_IDS.member6,
    userId: USER_IDS.member6,
    pickupLocationId: LOCATION_IDS.farm,
    signupPickupLocationId: LOCATION_IDS.farm,
    pickupDay: "Wednesday",
    subscriptionTierId: TIER_IDS.regular,
    subscriptionPaymentStatus: "paid",
    paidAt: "2026-04-24T09:18:00.000Z",
    friendPickupFirstName: null,
    friendPickupLastName: null,
    friendPickupPhone: null,
    createdAt: "2026-04-23T21:05:00.000Z",
    updatedAt: "2026-04-24T09:18:00.000Z",
  },
  {
    _id: PICKUP_IDS.member7,
    userId: USER_IDS.member7,
    pickupLocationId: LOCATION_IDS.malmo,
    signupPickupLocationId: LOCATION_IDS.malmo,
    pickupDay: "Thursday",
    subscriptionTierId: TIER_IDS.solidarity,
    // Failed payment — exercises the status filters in the members table.
    subscriptionPaymentStatus: "failed",
    friendPickupFirstName: null,
    friendPickupLastName: null,
    friendPickupPhone: null,
    createdAt: "2026-05-10T13:30:00.000Z",
    updatedAt: "2026-05-11T07:44:00.000Z",
  },
];

export const DEMO_HARVEST_PRODUCTS: DemoHarvestProduct[] = [
  {
    _id: HARVEST_PRODUCT_IDS.carrots,
    name: "Carrots",
    quantity: 140,
    price: 32,
    discount: null,
    createdAt: "2026-06-01T07:00:00.000Z",
    updatedAt: "2026-07-28T07:00:00.000Z",
  },
  {
    _id: HARVEST_PRODUCT_IDS.potatoes,
    name: "Potatoes",
    quantity: 220,
    price: 26,
    discount: 10,
    createdAt: "2026-06-01T07:01:00.000Z",
    updatedAt: "2026-07-28T07:01:00.000Z",
  },
  {
    _id: HARVEST_PRODUCT_IDS.kale,
    name: "Kale",
    quantity: 65,
    price: 38,
    discount: null,
    createdAt: "2026-06-01T07:02:00.000Z",
    updatedAt: "2026-07-27T07:02:00.000Z",
  },
  {
    _id: HARVEST_PRODUCT_IDS.leeks,
    name: "Leeks",
    quantity: 80,
    price: 34,
    discount: null,
    createdAt: "2026-06-01T07:03:00.000Z",
    updatedAt: "2026-07-26T07:03:00.000Z",
  },
  {
    _id: HARVEST_PRODUCT_IDS.beetroot,
    name: "Beetroot",
    quantity: 110,
    price: 29,
    discount: null,
    createdAt: "2026-06-01T07:04:00.000Z",
    updatedAt: "2026-07-25T07:04:00.000Z",
  },
  {
    _id: HARVEST_PRODUCT_IDS.onions,
    name: "Yellow Onions",
    quantity: 175,
    price: 24,
    discount: null,
    createdAt: "2026-06-01T07:05:00.000Z",
    updatedAt: "2026-07-24T07:05:00.000Z",
  },
  {
    _id: HARVEST_PRODUCT_IDS.squash,
    name: "Summer Squash",
    quantity: 95,
    price: 30,
    discount: 15,
    createdAt: "2026-06-01T07:06:00.000Z",
    updatedAt: "2026-07-29T07:06:00.000Z",
  },
  {
    _id: HARVEST_PRODUCT_IDS.spinach,
    name: "Spinach",
    quantity: 40,
    price: 42,
    discount: null,
    createdAt: "2026-06-01T07:07:00.000Z",
    updatedAt: "2026-07-23T07:07:00.000Z",
  },
  {
    _id: HARVEST_PRODUCT_IDS.fennel,
    name: "Fennel",
    quantity: 55,
    price: 36,
    discount: null,
    createdAt: "2026-06-01T07:08:00.000Z",
    updatedAt: "2026-07-22T07:08:00.000Z",
  },
  {
    _id: HARVEST_PRODUCT_IDS.cabbage,
    name: "White Cabbage",
    quantity: 130,
    price: 27,
    discount: null,
    createdAt: "2026-06-01T07:09:00.000Z",
    updatedAt: "2026-07-21T07:09:00.000Z",
  },
];

export const DEMO_HARVEST_LOGS: DemoHarvestLog[] = [
  {
    _id: HARVEST_LOG_IDS.week28,
    startDate: "2026-07-27",
    endDate: "2026-07-31",
    comment: "Strong week for squash. Spinach bolting in the heat — pulled early.",
    products: [
      { product: "Summer Squash", quantity: 95 },
      { product: "Carrots", quantity: 60 },
      { product: "Spinach", quantity: 18 },
      { product: "Kale", quantity: 25 },
    ],
    createdAt: "2026-07-31T16:00:00.000Z",
  },
  {
    _id: HARVEST_LOG_IDS.week27,
    startDate: "2026-07-20",
    endDate: "2026-07-24",
    comment: "First proper beetroot pull of the season.",
    products: [
      { product: "Beetroot", quantity: 70 },
      { product: "Yellow Onions", quantity: 90 },
      { product: "Leeks", quantity: 40 },
    ],
    createdAt: "2026-07-24T16:00:00.000Z",
  },
  {
    _id: HARVEST_LOG_IDS.week26,
    startDate: "2026-07-13",
    endDate: "2026-07-17",
    comment: "Rain mid-week slowed the harvest down.",
    products: [
      { product: "Potatoes", quantity: 120 },
      { product: "White Cabbage", quantity: 45 },
      { product: "Fennel", quantity: 30 },
    ],
    createdAt: "2026-07-17T16:00:00.000Z",
  },
  {
    _id: HARVEST_LOG_IDS.week25,
    startDate: "2026-07-06",
    endDate: "2026-07-10",
    comment: "",
    products: [
      { product: "Carrots", quantity: 80 },
      { product: "Kale", quantity: 40 },
      { product: "Spinach", quantity: 22 },
    ],
    createdAt: "2026-07-10T16:00:00.000Z",
  },
];

export const DEMO_ORDERS: DemoOrder[] = [
  {
    _id: ORDER_IDS.restaurang,
    companyRegistrationNumber: "556677-8899",
    // 1280 + 1300 + 76 line items, plus 180 shipping.
    totalPrice: 2836,
    deliveryMethod: "shipping",
    companyAddress: "Storgatan 14",
    companyPostalCode: "211 42",
    companyCity: "Malmö",
    shippingPrice: 180,
    status: "Pending",
    products: [
      { productName: "Carrots", unitPrice: 32, quantity: 40, totalPrice: 1280 },
      { productName: "Potatoes", unitPrice: 26, quantity: 50, totalPrice: 1300 },
      { productName: "Kale", unitPrice: 38, quantity: 2, totalPrice: 76 },
    ],
    createdAt: "2026-07-29T10:12:00.000Z",
    updatedAt: "2026-07-29T10:12:00.000Z",
  },
  {
    _id: ORDER_IDS.cafe,
    companyRegistrationNumber: "556677-8899",
    totalPrice: 1146,
    deliveryMethod: "pickup",
    status: "Completed",
    products: [
      { productName: "Summer Squash", unitPrice: 30, quantity: 25, totalPrice: 750 },
      { productName: "Fennel", unitPrice: 36, quantity: 11, totalPrice: 396 },
    ],
    createdAt: "2026-07-22T09:40:00.000Z",
    updatedAt: "2026-07-23T14:05:00.000Z",
  },
  {
    _id: ORDER_IDS.skola,
    companyRegistrationNumber: "556677-8899",
    // 1620 + 1740 line items, plus 180 shipping.
    totalPrice: 3540,
    deliveryMethod: "shipping",
    companyAddress: "Storgatan 14",
    companyPostalCode: "211 42",
    companyCity: "Malmö",
    shippingPrice: 180,
    status: "Cancelled",
    products: [
      { productName: "White Cabbage", unitPrice: 27, quantity: 60, totalPrice: 1620 },
      { productName: "Beetroot", unitPrice: 29, quantity: 60, totalPrice: 1740 },
    ],
    createdAt: "2026-07-15T08:20:00.000Z",
    updatedAt: "2026-07-16T11:00:00.000Z",
  },
];

export const DEMO_EVENTS: DemoEvent[] = [
  {
    _id: EVENT_IDS.harvestFestival,
    eventName: "Harvest Festival",
    eventStartTime: "2026-09-12T10:00:00.000Z",
    eventEndTime: "2026-09-12T16:00:00.000Z",
    locationOfEvent: "Alnarp Farm",
    numberOfAvailableTickets: 120,
    purchases: [
      {
        buyerName: "Astrid Lindqvist",
        buyerEmail: "astrid.lindqvist@example.com",
        quantity: 4,
        confirmationCode: "HF-2026-0001",
        comment: "Two children in the group.",
        purchasedAt: "2026-07-02T18:22:00.000Z",
      },
      {
        buyerName: "Tobias Krantz",
        buyerEmail: "tobias.krantz@example.com",
        quantity: 2,
        confirmationCode: "HF-2026-0002",
        purchasedAt: "2026-07-11T09:05:00.000Z",
      },
      {
        buyerName: "Maria Ferreira",
        buyerEmail: "maria.ferreira@example.com",
        quantity: 3,
        confirmationCode: "HF-2026-0003",
        comment: "Vegetarian lunch, please.",
        purchasedAt: "2026-07-19T14:40:00.000Z",
      },
    ],
  },
  {
    _id: EVENT_IDS.soilWorkshop,
    eventName: "Soil Health Workshop",
    eventStartTime: "2026-08-22T09:00:00.000Z",
    eventEndTime: "2026-08-22T12:30:00.000Z",
    locationOfEvent: "Alnarp Farm — Barn",
    numberOfAvailableTickets: 25,
    purchases: [
      {
        buyerName: "Sanna Holm",
        buyerEmail: "sanna.holm@example.com",
        quantity: 1,
        confirmationCode: "SW-2026-0001",
        purchasedAt: "2026-07-25T07:58:00.000Z",
      },
    ],
  },
];

export const DEMO_SEASON: DemoSeason = {
  _id: SEASON_ID,
  areWeInSeason: true,
  isBusinessOpen: true,
  applicationPeriod: "free-for-all",
  freeDeliveryThreshold: 1000,
  businessDeliveryPrice: 80,
  csaDeliveryPrice: 315,
  checkboxes: {
    printHarvestLogs: true,
    printOrders: true,
    updatePickupLocations: true,
    updateSubscriptionsAndTiers: true,
    updateCsaDeliveryPrice: false,
  },
  waitlist: [USER_IDS.unassigned],
  createdAt: "2026-01-15T09:00:00.000Z",
  updatedAt: "2026-07-01T09:00:00.000Z",
};

/** A deep copy of the seed data. The store calls this to (re)initialise itself. */
export function createDemoData(): DemoData {
  return JSON.parse(
    JSON.stringify({
      users: DEMO_USERS,
      pickups: DEMO_PICKUPS,
      pickupLocations: DEMO_PICKUP_LOCATIONS,
      subscriptions: DEMO_SUBSCRIPTIONS,
      tiers: DEMO_TIERS,
      harvestProducts: DEMO_HARVEST_PRODUCTS,
      harvestLogs: DEMO_HARVEST_LOGS,
      orders: DEMO_ORDERS,
      events: DEMO_EVENTS,
      season: DEMO_SEASON,
    }),
  ) as DemoData;
}
